import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Scale, Quote } from 'lucide-react';
import { Button } from '../ui/button';
import Breadcrumbs from '../Breadcrumbs';
import GeoFaq from '../GeoFaq';
import InternalLinks from '../InternalLinks';
import NotFound from '../NotFound';
import VersusCards from './VersusCards';
import CriteriaTable from './CriteriaTable';
import VisualizerBanner from '../visualizer/VisualizerBanner';
import { COMPARISONS, COMPARISON_LIST } from '../../data/comparisons';
import { buildFaqSchema, ORGANIZATION_REF } from '../../data/geoContent';
import { SERVICES } from '../../data/services';

const VergelijkPage = () => {
  const { slug } = useParams();
  const data = COMPARISONS[slug];

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!data) return <NotFound />;

  const url = `https://maxq.be/vergelijk/${slug}`;
  const service = SERVICES[data.service];
  const others = COMPARISON_LIST.filter((c) => c.slug !== slug).slice(0, 3);
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: data.h1,
    description: data.metaDescription,
    url,
    mainEntityOfPage: url,
    inLanguage: 'nl-BE',
    author: ORGANIZATION_REF,
    publisher: ORGANIZATION_REF,
    about: [{ '@type': 'Thing', name: data.a.name }, { '@type': 'Thing', name: data.b.name }],
    datePublished: '2026-06-01',
    dateModified: '2026-06-01',
  };

  return (
    <div data-testid="vergelijk-page">
      <Helmet>
        <title>{data.metaTitle}</title>
        <meta name="description" content={data.metaDescription} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={data.metaTitle} />
        <meta property="og:description" content={data.metaDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(buildFaqSchema(data.faqs))}</script>
      </Helmet>

      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Vergelijkingen', href: '/vergelijk' }, { label: data.title }]} />

      <section className="bg-[#f4efe9] py-16 md:py-24" data-testid="vergelijk-hero">
        <div className="max-w-5xl mx-auto px-4">
          <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#3a190b]/70 mb-5"><Scale className="h-4 w-4" aria-hidden="true" />Eerlijke vergelijking</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#202020] leading-[1.05] mb-8" data-testid="vergelijk-h1">{data.h1}</h1>
          <p className="text-base md:text-lg text-[#202020]/80 leading-relaxed max-w-3xl" data-geo="quick-answer" data-testid="vergelijk-summary">{data.summary}</p>
        </div>
      </section>

      <VersusCards a={data.a} b={data.b} slug={slug} />
      <CriteriaTable criteria={data.criteria} a={data.a} b={data.b} />
      <VisualizerBanner />

      <section className="py-16 bg-white" data-testid="vergelijk-verdict">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative rounded-2xl border-l-4 border-[#3a190b] bg-[#faf7f5] p-8 md:p-10">
            <Quote className="absolute -top-4 left-8 h-8 w-8 text-[#3a190b] bg-white rounded-full p-1.5 shadow" aria-hidden="true" />
            <h2 className="text-xl md:text-2xl font-bold text-[#202020] mb-3">Het advies van Max Q</h2>
            <p className="text-[#202020]/85 leading-relaxed">{data.verdict}</p>
            <p className="mt-4 text-xs text-[#202020]/55">Max Q · Gerhees 118, 3945 Ham · 4,8/5 op Google (39 reviews) · eigen vakmensen, geen onderaannemers</p>
          </div>
        </div>
      </section>

      <GeoFaq faqs={data.faqs} title={`Veelgestelde vragen: ${data.title.replace('?', '')}`} />

      <section className="py-16 bg-[#3a190b]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Twijfelt u nog? Wij komen kijken.</h2>
          <p className="text-white/80 mb-8">Gratis plaatsbezoek binnen 48 uur. We meten op en adviseren wat écht past bij uw woning en budget.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/start"><Button className="bg-white text-[#3a190b] hover:bg-gray-100 px-8 py-3" data-testid="vergelijk-cta">Gratis advies aanvragen<ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
            {service && <Link to={`/diensten/${data.service}`}><Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-3">Meer over {service.title.toLowerCase()}</Button></Link>}
          </div>
        </div>
      </section>

      <section className="py-12 bg-gray-50" data-testid="other-comparisons">
        <div className="max-w-7xl mx-auto px-4">
          <h3 className="font-bold text-[#202020] mb-4">Andere vergelijkingen</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            {others.map((c) => (
              <Link key={c.slug} to={`/vergelijk/${c.slug}`} className="group bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                <p className="text-xs uppercase tracking-wider text-[#3a190b]/60 mb-1">{c.a.name} vs {c.b.name}</p>
                <p className="font-semibold text-[#202020] group-hover:text-[#3a190b] transition-colors">{c.title}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <InternalLinks />
    </div>
  );
};

export default VergelijkPage;
